/**
 * Exterior Improvement Application status lifecycle (#363).
 *
 * Status is stored as an INTEGER in D1 (SQLite has no enum type). This module is
 * the single source of truth for the labels and the legal-transition graph, and is
 * imported by BOTH the API guard (`api/exterior-applications.ts`) and the UI so the
 * two never disagree — the same pattern as `utils/work-order-field-policy.ts`.
 *
 * Transitions are a map lookup, NOT `next > current`: NeedsAdditionalInfo (2) loops
 * back to Reviewed (1), so ordering-by-value would be wrong.
 */

export enum ExteriorApplicationStatus {
	/** Received, not yet triaged. */
	Submitted = 0,
	/** PM checked the request for completeness against the ruleset. */
	Reviewed = 1,
	/** Returned to the homeowner for more docs/detail (loops back to Reviewed). */
	NeedsAdditionalInfo = 2,
	/** Complete package sent to the HOA board — "Waiting for Board Approval". */
	SubmittedToBoard = 3,
	/** Board approved; PM logged the decision. A decider may reopen it to SubmittedToBoard. */
	Approved = 4,
	/** Board denied; PM logged the decision. A decider may reopen it to SubmittedToBoard. */
	Rejected = 5,
	/**
	 * Closed without a board decision — the homeowner dropped it, or it is otherwise dead. Reachable
	 * from every open state, needs a note, and is NOT a decision: no decision PDF is filed for it.
	 */
	Withdrawn = 6,
}

/** Human-readable label per status. */
export const APPLICATION_STATUS_LABEL: Record<ExteriorApplicationStatus, string> = {
	[ExteriorApplicationStatus.Submitted]: 'Submitted',
	[ExteriorApplicationStatus.Reviewed]: 'Reviewed',
	[ExteriorApplicationStatus.NeedsAdditionalInfo]: 'Needs Additional Info',
	[ExteriorApplicationStatus.SubmittedToBoard]: 'Waiting for Board Approval',
	[ExteriorApplicationStatus.Approved]: 'Approved',
	[ExteriorApplicationStatus.Rejected]: 'Rejected',
	[ExteriorApplicationStatus.Withdrawn]: 'Withdrawn',
};

/**
 * Legal next states per current state. Empty array = terminal.
 * Submitted → Reviewed | NeedsAdditionalInfo | Withdrawn
 * Reviewed → NeedsAdditionalInfo | SubmittedToBoard | Withdrawn
 * NeedsAdditionalInfo → Reviewed | SubmittedToBoard | Withdrawn
 * SubmittedToBoard → NeedsAdditionalInfo | Approved | Rejected | Withdrawn
 * Approved / Rejected → SubmittedToBoard (a REOPEN — undoing a premature decision)
 * Withdrawn → Submitted (a REOPEN — the homeowner changed their mind; triage it afresh)
 *
 * A reopen is one step back and no further, and the table alone does not authorize it: the
 * API also requires a user who may log decisions (`canLogDecision`) and clears the closing
 * stamp. Anything that moves the status on its own path — the board-approval letter — must
 * exclude closed requests itself rather than trust this table (see `canSendBoardApprovalLetter`).
 */
export const APPLICATION_STATUS_TRANSITIONS: Record<ExteriorApplicationStatus, ExteriorApplicationStatus[]> = {
	[ExteriorApplicationStatus.Submitted]: [
		ExteriorApplicationStatus.Reviewed,
		ExteriorApplicationStatus.NeedsAdditionalInfo,
		ExteriorApplicationStatus.Withdrawn,
	],
	[ExteriorApplicationStatus.Reviewed]: [
		ExteriorApplicationStatus.NeedsAdditionalInfo,
		ExteriorApplicationStatus.SubmittedToBoard,
		ExteriorApplicationStatus.Withdrawn,
	],
	[ExteriorApplicationStatus.NeedsAdditionalInfo]: [
		ExteriorApplicationStatus.Reviewed,
		ExteriorApplicationStatus.SubmittedToBoard,
		ExteriorApplicationStatus.Withdrawn,
	],
	[ExteriorApplicationStatus.SubmittedToBoard]: [
		ExteriorApplicationStatus.NeedsAdditionalInfo,
		ExteriorApplicationStatus.Approved,
		ExteriorApplicationStatus.Rejected,
		ExteriorApplicationStatus.Withdrawn,
	],
	[ExteriorApplicationStatus.Approved]: [ExteriorApplicationStatus.SubmittedToBoard],
	[ExteriorApplicationStatus.Rejected]: [ExteriorApplicationStatus.SubmittedToBoard],
	[ExteriorApplicationStatus.Withdrawn]: [ExteriorApplicationStatus.Submitted],
};

/** The two decision states the board can land on — the ones that file a decision PDF. */
export const APPLICATION_DECISION_STATUSES: ExteriorApplicationStatus[] = [
	ExteriorApplicationStatus.Approved,
	ExteriorApplicationStatus.Rejected,
];

/**
 * Every state that takes a request out of the open queue: the board's decisions plus a withdrawal.
 * Closing stamps who and when (`decided_by` / `decided_at`); leaving one is a reopen.
 */
export const APPLICATION_CLOSED_STATUSES: ExteriorApplicationStatus[] = [
	...APPLICATION_DECISION_STATUSES,
	ExteriorApplicationStatus.Withdrawn,
];

/** True when the request is still in flight — neither decided nor withdrawn. */
export const isApplicationOpen = (status: number): boolean =>
	!APPLICATION_CLOSED_STATUSES.includes(status as ExteriorApplicationStatus);

/** Where a reopen lands: the one move out of a closed state. Null for an open status. */
export const applicationReopenTarget = (status: number): ExteriorApplicationStatus | null =>
	(isApplicationOpen(status) ? null : APPLICATION_STATUS_TRANSITIONS[status as ExteriorApplicationStatus]?.[0] ?? null);

/** Every status still in flight, derived from the label map so a new status cannot be missed. */
export const OPEN_APPLICATION_STATUSES: ExteriorApplicationStatus[] = (
	Object.keys(APPLICATION_STATUS_LABEL).map(Number) as ExteriorApplicationStatus[]
).filter(isApplicationOpen);

/**
 * The list query behind the Exterior Improvements rail and every open-work view (#581).
 *
 * Open statuses only, at the SERVER. Unfiltered, decided requests accumulate forever and
 * eventually push open work past the cap — the rail would then under-count the very queue
 * it exists to show. Module constants, so every caller shares one TanStack key (and one
 * fetch) without having to remember to memoize an inline object.
 */
export const OPEN_APPLICATIONS_QUERY = { status: OPEN_APPLICATION_STATUSES.join(','), limit: 500 };

/** Approved, rejected and withdrawn — the Archived view. */
export const ARCHIVED_APPLICATIONS_QUERY = { status: APPLICATION_CLOSED_STATUSES.join(','), limit: 500 };

/** True when the status value is a defined member of the enum. */
export const isApplicationStatus = (value: unknown): value is ExteriorApplicationStatus =>
	typeof value === 'number' && value in APPLICATION_STATUS_LABEL;

/** True when moving from `current` to `next` is a legal transition (or a no-op). */
export const canTransitionApplicationStatus = (
	current: ExteriorApplicationStatus,
	next: ExteriorApplicationStatus,
): boolean => current === next || (APPLICATION_STATUS_TRANSITIONS[current]?.includes(next) ?? false);

/**
 * The board letter is valid only for a legal board submission or a resend while with the board.
 *
 * Never for a decided request, although the table lets one move to SubmittedToBoard: that move
 * is a REOPEN, reserved for a decider, and the letter's send moves the status without that check.
 * "It's with the board" is also simply false once the board has decided.
 */
export const canSendBoardApprovalLetter = (status: number): boolean =>
	isApplicationStatus(status)
	&& isApplicationOpen(status)
	&& canTransitionApplicationStatus(status, ExteriorApplicationStatus.SubmittedToBoard);

/** Label for a raw integer status, tolerant of unknown values. */
export const applicationStatusLabel = (status: number): string =>
	APPLICATION_STATUS_LABEL[status as ExteriorApplicationStatus] ?? `Unknown (${status})`;

/**
 * "a" or "an" for a status label, so generated prose reads correctly.
 *
 * Lives here beside the labels because that is what decides it: "Approved" is the one label
 * in this enum that starts with a vowel, and it is also one of only two statuses that can
 * reach the "cannot move to…" message. A hardcoded "A" shipped "A Approved request cannot
 * move to Needs Additional Info", found by reading the real string on a preview walk.
 *
 * Letter-based, not phonetic. Every label here is a plain English word, so the honest-sounding
 * exceptions ("an hour", "a unicorn") cannot arise; a general-purpose a/an library would be
 * more machinery than this needs.
 */
export const indefiniteArticle = (label: string): string =>
	(/^[aeiou]/i.test(label.trim()) ? 'An' : 'A');
